import type { RequestContext } from "./types.js";
import { isNodeEnvironment } from "./runtime.js";

type RequestContextStore = {
  getStore(): RequestContext | undefined;
  run<T>(context: RequestContext, fn: () => T): T;
};

let fallbackStore: RequestContextStore | null = null;
let nodeStore: RequestContextStore | null = null;

function getFallbackStore(): RequestContextStore {
  if (!fallbackStore) {
    let current: RequestContext | undefined;
    fallbackStore = {
      getStore: () => current,
      run: (context, fn) => {
        const prev = current;
        current = context;
        try {
          return fn();
        } finally {
          current = prev;
        }
      },
    };
  }
  return fallbackStore;
}

function getNodeStore(): RequestContextStore {
  if (!nodeStore) {
    const getBuiltinModule = (process as {
      getBuiltinModule?: (name: string) => unknown;
    }).getBuiltinModule;

    const asyncHooks = getBuiltinModule?.("async_hooks") as
      | (typeof import("node:async_hooks") & {
          AsyncLocalStorage?: new <T>() => {
            getStore(): T | undefined;
            run<R>(store: T, callback: () => R): R;
          };
        })
      | undefined;

    if (asyncHooks?.AsyncLocalStorage) {
      const als = new asyncHooks.AsyncLocalStorage<RequestContext>();
      nodeStore = {
        getStore: () => als.getStore(),
        run: (context, fn) => als.run(context, fn),
      };
      return nodeStore;
    }

    // Fallback for runtimes where async_hooks isn't available.
    nodeStore = getFallbackStore();
  }
  return nodeStore;
}

function activeStore(): RequestContextStore {
  return isNodeEnvironment() ? getNodeStore() : getFallbackStore();
}

export function runWithRequestContext<T>(
  context: RequestContext,
  fn: () => T,
): T {
  return activeStore().run(context, fn);
}

export function getRequestContext(): RequestContext | undefined {
  return activeStore().getStore();
}

export function requestContextFromFetch(request: Request): RequestContext {
  return {
    url: request.url,
    route: new URL(request.url).pathname,
    userAgent: request.headers.get("user-agent") ?? undefined,
    requestId: request.headers.get("x-request-id") ?? undefined,
  };
}
